import { useTranslation } from 'react-i18next'
import { type GroupMemberStatus } from '@fs/zion-tree-types'

export type TreeRole = 'administrator' | 'co-owner' | 'contributor' | 'member' | 'owner'

type UseTreeRoleParams = {
  isResearcherTree?: boolean
  memberCisId?: string
  memberStatus?: GroupMemberStatus
  ownerCisId?: string
}

const treeRoleLabelKeys: Record<TreeRole, string> = {
  administrator: 'group-management.label.administrator',
  'co-owner': 'group-management.label.co-owner',
  contributor: 'group-management.label.contributor',
  member: 'group-management.label.member',
  owner: 'group-management.owner.label',
}

/**
 * Resolves a member's role on a tree for display and returns the translated label.
 * The vocabulary differs by tree type:
 *
 * - Family group trees use Administrator / Member.
 * - Research trees use Owner / Co-owner / Contributor. 'ADMIN' covers both the owner and the
 *   co-owners there, so telling them apart means comparing the tree's `ownerCisId` against the
 *   member's cisId.
 */
export function useTreeRole({
  isResearcherTree,
  memberCisId,
  memberStatus,
  ownerCisId,
}: UseTreeRoleParams): string | undefined {
  const { t } = useTranslation()

  if (!isResearcherTree) {
    if (memberStatus === 'ADMIN') return t(treeRoleLabelKeys.administrator)
    if (memberStatus === 'MEMBER') return t(treeRoleLabelKeys.member)
    return undefined
  }

  if (memberStatus === 'ADMIN') {
    // Without both IDs, owner and co-owner are indistinguishable. ownerCisId loads async, so
    // guessing would mislabel the real owner until that fetch resolves.
    if (!memberCisId || !ownerCisId) return undefined
    if (ownerCisId === memberCisId) return t(treeRoleLabelKeys.owner)
    return t(treeRoleLabelKeys['co-owner'])
  }
  if (memberStatus === 'MEMBER') return t(treeRoleLabelKeys.contributor)

  return undefined
}
